import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Scale, Calendar, User, Mail, Phone, FileText, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Ticket, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_318b698c-05e0-45f0-aa51-d3d773434be0/artifacts/p0qxibr4_BETO%20FISCHER%20%282%29.svg";

export default function WithdrawalPage() {
  const [legalText, setLegalText] = useState('');
  const [showLegalText, setShowLegalText] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [withdrawalId, setWithdrawalId] = useState('');
  
  const [formData, setFormData] = useState({
    withdrawal_type: 'ticket',
    full_name: '',
    email: '',
    dni: '',
    phone: '',
    purchase_date: '',
    purchase_description: '',
    reason: '',
    order_number: ''
  });

  useEffect(() => {
    // Fetch legal text
    const fetchLegalText = async () => {
      try {
        const response = await api.get('/withdrawal/legal-text');
        setLegalText(response.data.text);
      } catch (error) {
        console.error('Error fetching legal text:', error);
      }
    };
    fetchLegalText();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await api.post('/withdrawal/request', formData);
      setWithdrawalId(response.data.withdrawal_id);
      setIsSubmitted(true);
      toast.success('Solicitud enviada correctamente');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al enviar la solicitud';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Calculate max date (today) and min date (10 days ago)
  const today = new Date().toISOString().split('T')[0];
  const tenDaysAgo = new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-[#111] rounded-2xl p-8 text-center border border-white/10"
        >
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          
          <h1 className="text-2xl font-bold text-white mb-2">Solicitud Recibida</h1>
          
          <p className="text-white/60 mb-6">
            Tu solicitud de arrepentimiento ha sido registrada correctamente.
          </p>
          
          <div className="bg-[#1a1a1a] rounded-lg p-4 mb-6">
            <p className="text-white/40 text-sm mb-1">Número de solicitud</p>
            <p className="text-[#D4AF37] text-xl font-mono font-bold">{withdrawalId}</p>
          </div>
          
          <p className="text-white/50 text-sm mb-6">
            Recibirás un email de confirmación. Un representante se comunicará contigo dentro de las <strong className="text-white">48 horas hábiles</strong>.
          </p>
          
          <Link to="/">
            <Button className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black">
              Volver al inicio
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="text-sm">Volver</span>
          </Link>
          <img src={LOGO_URL} alt="Bravo Producciones" className="h-8" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Scale className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Derecho de Arrepentimiento</h1>
          <p className="text-white/60">Ley 24.240 de Defensa del Consumidor - República Argentina</p>
        </motion.div>

        {/* Legal Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-xl p-4 mb-8"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white/80 text-sm">
                Tenés <strong className="text-[#D4AF37]">10 días corridos</strong> desde la fecha de compra para ejercer tu derecho de arrepentimiento sin necesidad de justificar el motivo.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Legal Text Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
        >
          <button
            onClick={() => setShowLegalText(!showLegalText)}
            className="w-full flex items-center justify-between p-4 bg-[#111] rounded-xl border border-white/10 hover:border-white/20 transition-colors"
          >
            <span className="text-white/80 font-medium flex items-center gap-2">
              <FileText size={18} className="text-[#D4AF37]" />
              Ver texto legal completo
            </span>
            {showLegalText ? <ChevronUp size={20} className="text-white/40" /> : <ChevronDown size={20} className="text-white/40" />}
          </button>
          
          {showLegalText && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2 p-6 bg-[#111] rounded-xl border border-white/10 prose prose-invert prose-sm max-w-none"
            >
              <ReactMarkdown>{legalText}</ReactMarkdown>
            </motion.div>
          )}
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-[#111] rounded-2xl border border-white/10 p-6 md:p-8"
        >
          <h2 className="text-xl font-semibold text-white mb-6">Formulario de Solicitud</h2>

          {/* Type Selection */}
          <div className="mb-6">
            <Label className="text-white/80 mb-3 block">Tipo de compra *</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, withdrawal_type: 'ticket' }))}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  formData.withdrawal_type === 'ticket'
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'border-white/10 bg-white/5 text-white/60 hover:border-white/20'
                }`}
              >
                <Ticket size={24} />
                <span className="font-medium">Entrada / Ticket</span>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, withdrawal_type: 'subscription' }))}
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                  formData.withdrawal_type === 'subscription'
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'border-white/10 bg-white/5 text-white/60 hover:border-white/20'
                }`}
              >
                <CreditCard size={24} />
                <span className="font-medium">Suscripción</span>
              </button>
            </div>
          </div>

          {/* Personal Info */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-white/80 mb-2 block flex items-center gap-2">
                <User size={16} className="text-[#D4AF37]" />
                Nombre completo *
              </Label>
              <Input
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="Juan Pérez"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block flex items-center gap-2">
                <Mail size={16} className="text-[#D4AF37]" />
                Email *
              </Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="juan@email.com"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-white/80 mb-2 block">DNI *</Label>
              <Input
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                placeholder="12.345.678"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block flex items-center gap-2">
                <Phone size={16} className="text-[#D4AF37]" />
                Teléfono *
              </Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+54 11 1234-5678"
                required
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          {/* Purchase Info */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-white/80 mb-2 block flex items-center gap-2">
                <Calendar size={16} className="text-[#D4AF37]" />
                Fecha de compra *
              </Label>
              <Input
                type="date"
                name="purchase_date"
                value={formData.purchase_date}
                onChange={handleChange}
                min={tenDaysAgo}
                max={today}
                required
                className="bg-white/5 border-white/10 text-white [color-scheme:dark]"
              />
              <p className="text-white/40 text-xs mt-1">Solo compras de los últimos 10 días</p>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Número de orden (opcional)</Label>
              <Input
                name="order_number"
                value={formData.order_number}
                onChange={handleChange}
                placeholder="ORD-12345"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>
          </div>

          <div className="mb-6">
            <Label className="text-white/80 mb-2 block">Descripción de la compra *</Label>
            <Textarea
              name="purchase_description"
              value={formData.purchase_description}
              onChange={handleChange}
              placeholder="Ej: Entrada para el evento 'Fiesta de Fin de Año' del 31/12/2025, 2 entradas VIP..."
              required
              minLength={10}
              rows={3}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
          </div>

          <div className="mb-8">
            <Label className="text-white/80 mb-2 block">Motivo (opcional)</Label>
            <Textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder="Si querés, contanos el motivo de tu solicitud..."
              rows={2}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
            />
            <p className="text-white/40 text-xs mt-1">No es obligatorio justificar el arrepentimiento</p>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold py-6 text-lg"
            data-testid="withdrawal-submit-btn"
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
          </Button>

          <p className="text-white/40 text-xs text-center mt-4">
            Al enviar este formulario, declarás que la información proporcionada es verdadera y completa.
          </p>
        </motion.form>
      </main>
    </div>
  );
}
